/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerLayout
 * ---------------
 * Shell for every /organizer route: the glass rail plus an Outlet.
 *
 * The Dawn token set is applied here, on the shell element, so all organizer
 * screens inherit it through var(--org-*) without touching the app-wide
 * ThemeContext — the rest of the product keeps whatever theme the user picked.
 *
 * On a phone (768px and narrower) the rail is not shown at all: a slim top bar
 * carries a menu button, and the rail slides in as a drawer only when that is
 * tapped. It closes on navigation, on the backdrop, and on Escape.
 */

import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import OrganizerSidebar from '../../components/organizer/OrganizerSidebar';
import ORGANIZER_TOKENS from '../../theme/organizerTokens';
import envConfig from '../../config/envConfig';
import '../../styles/organizer.css';

const NARROW_QUERY = '(max-width: 768px)';

function useNarrow() {
  const [narrow, setNarrow] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(NARROW_QUERY).matches : false
  ));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

function OrganizerLayout() {
  const narrow = useNarrow();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setMenuOpen(false); }, [pathname, narrow]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className="org-shell" style={ORGANIZER_TOKENS}>
      <OrganizerSidebar
        narrow={narrow}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      />
      {narrow && menuOpen && (
        <div className="org-scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      )}
      <div className="org-main">
        {narrow && (
          <header className="org-mobilebar">
            <button
              type="button"
              className="org-burger"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="org-rail"
              onClick={() => setMenuOpen(true)}
            >
              <i className="bi bi-list" aria-hidden="true" />
            </button>
            <span className="org-mark" aria-hidden="true">AH</span>
            <b className="org-mobilename">{envConfig.appName || 'AthloHive'}</b>
          </header>
        )}
        <Outlet />
      </div>
    </div>
  );
}

export default OrganizerLayout;
